import React from "react";
import ServicePageLayout, { H2, H3, P, UL } from "./ServicePageLayout";

export const meta = {
  title: "Commercial Networking Alberta | Business Network Installation | Epicom Systems",
  metaDescription:
    "Commercial network design, cabling, switching and routing across Southern Alberta. VLANs, PoE, network segmentation and reliable business infrastructure built on Ubiquiti UniFi.",
  canonicalPath: "/commercial-networking",
  jsonLd: {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://epicom.ca/commercial-networking#service",
    "name": "Commercial Network Design and Installation",
    "serviceType": "Business network design, cabling, switching and routing",
    "provider": { "@type": "LocalBusiness", "@id": "https://epicom.ca/#business" },
    "areaServed": { "@type": "AdministrativeArea", "name": "Southern Alberta" },
    "description":
      "Commercial and industrial network design, structured cabling, switching, routing, VLANs and PoE across Southern Alberta — built to be reliable day-to-day and maintainable long-term, primarily on Ubiquiti UniFi.",
    "url": "https://epicom.ca/commercial-networking",
  },
};

export default function CommercialNetworking() {
  return (
    <ServicePageLayout
      title={meta.title}
      metaDescription={meta.metaDescription}
      canonicalPath={meta.canonicalPath}
      overline="Commercial Networking"
      h1="Commercial Networks That Do What They’re Supposed To"
      intro="A business network is one of those things nobody notices until it breaks. Epicom designs and installs commercial networks across Southern Alberta with the specific goal of being invisible the rest of the time — solid cabling, sensible switching, real routing, and the kind of segmentation that keeps a compromised device from becoming a whole-site outage."
      jsonLd={meta.jsonLd}
      relatedLinks={[
        { to: "/unifi-installation", label: "UniFi Installation" },
        { to: "/business-wifi", label: "Business Wi-Fi" },
        { to: "/point-to-point-wireless", label: "Point-to-Point Wireless" },
      ]}
    >
      <H2>What a proper commercial network is</H2>
      <UL>
        <><strong className="text-white/90">Structured cabling</strong> — Cat6/Cat6A runs, terminated to patch panels, tested. Cable is the part everything else depends on and the part it’s hardest to redo after the fact.</>
        <><strong className="text-white/90">Switching</strong> — PoE and non-PoE switches sized for the site, with room to grow. Uplinks between switches are actually uplinked, not chained through a random port on the back.</>
        <><strong className="text-white/90">Routing and gateway</strong> — a real gateway/router, with firewall rules and IDS/IPS where appropriate. Not a $99 consumer box quietly hitting its session-table limit at 3 pm.</>
        <><strong className="text-white/90">VLANs and segmentation</strong> — staff, guests, cameras, VoIP, point-of-sale, IoT — each in their own segment, allowed to talk only to what they legitimately need to.</>
        <><strong className="text-white/90">Wi-Fi</strong> — access points on Ethernet backhaul, planned for the space, tied into the same environment. See our <a href="/business-wifi" className="text-signal hover:underline">Business Wi-Fi</a> page for depth.</>
        <><strong className="text-white/90">Remote management</strong> — the network is visible and adjustable from one interface, so changes and troubleshooting don’t need a truck roll.</>
      </UL>

      <H2>Common project types</H2>
      <UL>
        <>Offices, professional practices and clinics outgrowing consumer routers and mesh Wi-Fi.</>
        <>Industrial and manufacturing sites that need reliable shop-floor networking, wired cameras and separate office/administrative networks.</>
        <>Multi-building commercial sites where every building needs to look like part of the same network — often stitched together with <a href="/point-to-point-wireless" className="text-signal hover:underline">wireless bridges</a>.</>
        <>Retail sites with staff, guest, and point-of-sale traffic that should absolutely not share a single flat network.</>
        <>Farms and acreages that have grown into effectively small businesses — house, shop, main office, feed/livestock buildings — and need the network to reflect that.</>
      </UL>

      <H2>Why we build on UniFi</H2>
      <P>
        Most of our commercial networks are built on Ubiquiti UniFi — gateway, switching,
        Wi-Fi and (where appropriate) cameras and access all in one environment. The
        combination of capability, price and long-term maintainability is a good fit for
        the size of business we typically work with. We are experienced installers of these
        products, not Ubiquiti authorized dealers or certified Ubiquiti partners, and we
        buy through normal channels. If a job is genuinely better served by different gear,
        we say so.
      </P>

      <H2>Segmentation is not optional</H2>
      <H3>Staff, guests, cameras, POS</H3>
      <P>
        The single most common problem we see when we walk into an existing network is
        that everything shares one flat subnet — laptops, printers, a smart TV somebody
        plugged in, a couple of consumer-grade cameras, the office thermostat and the
        payment terminal. Segmentation is what keeps one compromised IoT device from being
        able to reach the accounting workstation. It also keeps the loud IoT traffic off
        the same broadcast domain as the important stuff.
      </P>
      <H3>Sensible defaults, minimal exceptions</H3>
      <P>
        We segment by function, then only open the specific paths that legitimately need to
        exist between segments. That keeps the rule set small enough to actually be
        readable, which matters when something has to change six months later.
      </P>

      <H2>Reliability comes from boring choices</H2>
      <P>
        A network that stays up for years does so because of unglamorous decisions:
        properly terminated cabling, real UPS on the head-end gear, PoE budgets that aren’t
        maxed out, switches with enough headroom, a gateway that isn’t running at 90% CPU.
        We over-spec the small stuff so the site doesn’t start throwing intermittent problems
        the day after the warranty ends.
      </P>

      <H2>Working with Epicom on a network project</H2>
      <P>
        Network projects start with a walk-through and a real conversation about how the
        business uses the network — users, buildings, applications, phone systems, cameras,
        point-of-sale, anything wireless, anything remote. From that we produce a design
        we can quote against and a rough sequence for the install so the site can keep
        operating while we work. When we hand it over, we hand over documentation and the
        one login you need to see everything.
      </P>
    </ServicePageLayout>
  );
}
